import React, { useState } from 'react';
import { ChevronDownIcon, TypeIcon, BoldIcon, ItalicIcon, UnderlineIcon, LinkIcon, QuoteIcon, PaletteIcon, AlignLeftIcon, ListIcon } from 'lucide-react';
import { ChannelSelector } from './ChannelSelector';
import { ZoneSelector } from './ZoneSelector';
interface LegacyNoticeComposerProps {
  legacyMode: boolean;
  onToggleLegacy: (value: boolean) => void;
}
export function LegacyNoticeComposer({
  legacyMode,
  onToggleLegacy
}: LegacyNoticeComposerProps) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  return <div className="font-body" data-id="element-2025" data-mp="243fq51jaeeqg">
      {/* Header row with version toggle */}
      <div className="flex items-center justify-between mb-6" data-id="element-2026" data-mp="291hbxu83ks5m">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-2027" data-mp="28yv0vklzzuin">
          Contenu de l'avis
        </h2>
        <label className="flex items-center gap-2 cursor-pointer select-none" data-id="element-2028" data-mp="emy6pjw68ajw">
          <span className="text-xs font-medium text-slate-400" data-id="element-2029" data-mp="ep178xuy9m2v">
            Version classique
          </span>
          <button type="button" role="switch" aria-checked={legacyMode} onClick={() => onToggleLegacy(!legacyMode)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${legacyMode ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-2030" data-mp="1eyhmiq5vnyz1">
            <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${legacyMode ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-2031" data-mp="1ymgwvzwj543x" />
          </button>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-2032" data-mp="1oo14ltlrnosu">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-7" data-id="element-2033" data-mp="i9s1ws2v8s9d">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6" data-id="element-2034" data-mp="d9449nk4td66">
            {/* Category */}
            <div className="mb-5" data-id="element-2035" data-mp="eprnu851szhz">
              <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-2036" data-mp="eqsyibi5jjz6">
                <span className="text-red-500" data-id="element-2037" data-mp="joba69nk0dav">*</span> Sélectionner une
                catégorie
              </label>
              <div className="relative" data-id="element-2038" data-mp="5p34qz0ss8y8">
                <select className="block w-full pl-3 pr-10 py-2.5 text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] rounded-lg appearance-none bg-white text-slate-500" data-id="element-2039" data-mp="aoo95oyql9ic">
                  <option value="" data-id="element-2040" data-mp="qs0d3t9uqne">Catégorie</option>
                  <option data-id="element-2041" data-mp="scsynw0zzim">Urgence</option>
                  <option data-id="element-2042" data-mp="3j5amod285w4c">Eau potable</option>
                  <option data-id="element-2043" data-mp="3j2enymijz3eu">Travaux</option>
                  <option data-id="element-2044" data-mp="3n5l3ylvgw8yi">Collecte</option>
                  <option data-id="element-2045" data-mp="3n6mhcwzyh7je">Loisirs</option>
                  <option data-id="element-2046" data-mp="396lexd41ls1t">Bibliothèque</option>
                  <option data-id="element-2047" data-mp="396trt5060xd0">Permis</option>
                  <option data-id="element-2048" data-mp="3e5esf5jbxj1z">Taxes</option>
                  <option data-id="element-2049" data-mp="3e4d3py9e4y1s">Événement</option>
                  <option data-id="element-2050" data-mp="3e7hjzu04rk8t">Information générale</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400" data-id="element-2051" data-mp="2uk4r4zu6ge51">
                  <ChevronDownIcon className="h-4 w-4" data-id="element-2052" data-mp="2pmmfv2sotmtv" />
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="mb-5" data-id="element-2053" data-mp="2zig3pibux2qy">
              <div className="flex items-center justify-between mb-2" data-id="element-2054" data-mp="2zh4rcu4cimo8">
                <label className="block text-sm font-semibold text-slate-700" data-id="element-2055" data-mp="3dhffvqi6np68">
                  <span className="text-red-500" data-id="element-2056" data-mp="38jxgaqq22maw">*</span> Titre
                </label>
                <span className="text-xs text-slate-400" data-id="element-2057" data-mp="3n8ac9np5855e">
                  {title.length}/60 caractères
                </span>
              </div>
              <input type="text" value={title} maxLength={60} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] outline-none text-sm" data-id="element-2058" data-mp="3iaibe46it8p2" />
            </div>

            {/* Summary */}
            <div className="mb-5" data-id="element-2059" data-mp="1pjwt1y6z3m54">
              <div className="flex items-center justify-between mb-2" data-id="element-2060" data-mp="1pividutvd1nx">
                <label className="block text-sm font-semibold text-slate-700" data-id="element-2061" data-mp="1ui82w8x9pwnc">
                  <span className="text-red-500" data-id="element-2062" data-mp="1zbkgzp4bml1u">*</span> Description sommaire
                </label>
                <span className="text-xs text-slate-400" data-id="element-2063" data-mp="248kmnyx9dlsf">
                  {summary.length}/160 caractères
                </span>
              </div>
              <textarea value={summary} maxLength={160} onChange={e => setSummary(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] outline-none text-sm resize-none" data-id="element-2064" data-mp="16o6r2az1ipr8" />
            </div>

            {/* Detailed Description */}
            <div data-id="element-2065" data-mp="2rzq0k8jn2403">
              <div className="flex items-center justify-between mb-2" data-id="element-2066" data-mp="2ry576m928r3t">
                <label className="block text-sm font-semibold text-slate-700" data-id="element-2067" data-mp="2w0ibr7c2miz0">
                  <span className="text-red-500" data-id="element-2068" data-mp="2w2mq6j7lj0dj">*</span> Description détaillée
                </label>
                <span className="text-xs text-slate-400" data-id="element-2069" data-mp="gbtk4la73u2a">
                  {description.length}/∞ caractères
                </span>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-2070" data-mp="bjtpn8cw9td3">
                <div className="flex items-center gap-1 px-3 py-2 bg-slate-50 border-b border-slate-200 flex-wrap text-slate-500" data-id="element-2071" data-mp="6mtggokr0ahr">
                  <button className="flex items-center gap-1 px-1.5 py-1 hover:bg-slate-200 rounded text-xs font-medium" data-id="element-2072" data-mp="1o8ikvsyy2wh">
                    Sans Serif <ChevronDownIcon className="w-3 h-3" data-id="element-2073" data-mp="3orn3d7vwvjn1" />
                  </button>
                  <span className="w-px h-5 bg-slate-300 mx-1" data-id="element-2074" data-mp="3othmqhrjb3p0"></span>
                  <button className="flex items-center gap-1 px-1.5 py-1 hover:bg-slate-200 rounded text-xs font-medium" data-id="element-2075" data-mp="3ju4oz1z4kc7n">
                    Normal <ChevronDownIcon className="w-3 h-3" data-id="element-2076" data-mp="1mxxgw6s3l3eg" />
                  </button>
                  <span className="w-px h-5 bg-slate-300 mx-1" data-id="element-2077" data-mp="1hz43eect9cea"></span>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Police" data-id="element-2078" data-mp="2150gsew0b14w">
                    <TypeIcon className="w-4 h-4" data-id="element-2079" data-mp="25xk6ubbvroql" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Gras" data-id="element-2080" data-mp="1s5dn6f9iqzme">
                    <BoldIcon className="w-4 h-4" data-id="element-2081" data-mp="1x489hr6p36b9" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Italique" data-id="element-2082" data-mp="1x246rckj8dck">
                    <ItalicIcon className="w-4 h-4" data-id="element-2083" data-mp="2kuklpe8z3p7n" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Souligné" data-id="element-2084" data-mp="2ou3banbqoogt">
                    <UnderlineIcon className="w-4 h-4" data-id="element-2085" data-mp="2auk3xwqr5h5k" />
                  </button>
                  <span className="w-px h-5 bg-slate-300 mx-1" data-id="element-2086" data-mp="2ftwzr6zlpyka"></span>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Lien" data-id="element-2087" data-mp="zgg80l0ltixv">
                    <LinkIcon className="w-4 h-4" data-id="element-2088" data-mp="zit03r02t9on" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Citation" data-id="element-2089" data-mp="14h46frr5x224">
                    <QuoteIcon className="w-4 h-4" data-id="element-2090" data-mp="wugwmvtjz489" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Couleur" data-id="element-2091" data-mp="rvvv8t2keei8">
                    <PaletteIcon className="w-4 h-4" data-id="element-2092" data-mp="15wyk1dky22wu" />
                  </button>
                  <span className="w-px h-5 bg-slate-300 mx-1" data-id="element-2093" data-mp="10xve7qx53snt"></span>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Alignement" data-id="element-2094" data-mp="10vixti715qcv">
                    <AlignLeftIcon className="w-4 h-4" data-id="element-2095" data-mp="d8asj09lqhau" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 rounded" title="Liste" data-id="element-2096" data-mp="980oht5de0ym">
                    <ListIcon className="w-4 h-4" data-id="element-2097" data-mp="myvy57np0m39" />
                  </button>
                </div>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} placeholder="Insérer une description ici" className="w-full px-4 py-3 outline-none text-sm resize-none placeholder-slate-400" data-id="element-2098" data-mp="i62tejs85dr6" />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-5 space-y-6" data-id="element-2099" data-mp="313u4fgxo9u5u">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6" data-id="element-2100" data-mp="3532y6lspps1e">
            <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-2101" data-mp="3a2z26l1etjgy">
              Durée d'avis
            </h3>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-2102" data-mp="3f2259zxtxfma">
              Période
            </div>
            <button className="text-sm font-bold text-[#4F46E5] uppercase tracking-wide hover:text-[#4338CA] transition-colors" data-id="element-2103" data-mp="3k0my3ear4x0p">
              Entrer la durée
            </button>
          </div>

          <ChannelSelector data-id="element-2104" data-mp="3stqgm1098027" />

          <ZoneSelector data-id="element-2105" data-mp="1mw5tbfqzh7x" />

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6" data-id="element-2106" data-mp="1nffk4v8uzav">
            <h3 className="text-lg font-bold text-[#0F172A] font-heading mb-4" data-id="element-2107" data-mp="6m8s9v2pim9z">
              Aperçu
            </h3>
            <div className="w-full aspect-[4/3] bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center" data-id="element-2108" data-mp="bkabqh41onlz">
              <span className="text-2xl font-bold text-slate-300 lowercase" data-id="element-2109" data-mp="1w4p519e188we">
                bc
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}